/**
 * Dependency imports
 */
import React from 'react';
import {
  Anchor,
  Box,
  BoxProps,
  Divider,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  Alert,
  PasswordInput,
  LoadingOverlay,
} from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Joi from 'joi';

/**
 * Local imports
 */
import Button from '@/components/Button';
import FAIcon from '@/components/FAIcon';
import { useEmailSignup } from '@/hooks/use-email-signup';
import { useProviderAuth } from '@/hooks/use-provider-auth';
import EmailSVG from '../../../public/img/icons/email.svg';
import GoogleSVG from '../../../public/img/icons/social/google.svg';

/**
 * Type definitions
 */
interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
}

interface Props {
  onLoginRequest?: () => void;
  onCloseRequest?: () => void;
  onSignupSuccess?: () => void;
  children?: React.ReactNode;
}

export type SignupFormProps = Props & BoxProps;

/**
 * Styled components
 */
const Root = styled(Box, {
  shouldForwardProp: (prop: PropertyKey) => ![''].includes(prop.toString()),
})<Partial<SignupFormProps>>((props) => css`
  position: relative;
  min-height: 400px;
`);

/**
 * Password requirement component
 */
const PasswordRequirement = ({ meets, label }: { meets: boolean; label: string }) => {
  return (
    <Text
      component="div"
      c={meets ? 'teal' : 'red'}
      style={{ display: 'flex', alignItems: 'center' }}
      mt={7}
      size="sm"
    >
      <FAIcon
        name={meets ? 'check' : 'xmark'}
        size={14}
        style={{ marginRight: '10px' }}
      />
      <Box ml={10}>{label}</Box>
    </Text>
  );
};

/**
 * Password requirements check
 */
const requirements = [
  { re: /[0-9]/, label: 'Includes number' },
  { re: /[a-z]/, label: 'Includes lowercase letter' },
  { re: /[A-Z]/, label: 'Includes uppercase letter' },
  { re: /[@$!%*#?&]/, label: 'Includes special character (@$!%*#?&)' },
  { re: /.{8,}/, label: 'At least 8 characters' },
];

/**
 * SignupForm Component
 */
const SignupForm: React.FC<SignupFormProps> = ({
  onLoginRequest,
  onCloseRequest,
  onSignupSuccess,
  ...props
}) => {
  const [emailFormVisible, setEmailFormVisible] = React.useState(false);
  const [emailSignup, emailSignupResponse] = useEmailSignup();
  const [googleAuth, googleAuthResponse] = useProviderAuth('google');
  const [popoverOpened, setPopoverOpened] = React.useState(false);
  const [resendingEmail, setResendingEmail] = React.useState(false);
  const [resendSuccess, setResendSuccess] = React.useState(false);
  const [resendError, setResendError] = React.useState<string | null>(null);
  const [remainingCooldown, setRemainingCooldown] = React.useState<number>(0);
  const [resendCount, setResendCount] = React.useState<number>(0);

  // Enhanced password validation regex
  const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,30}$/;

  const schema = Joi.object({
    email: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .messages({
        'string.empty': 'Please enter an e-mail address',
        'string.email': 'Please enter a valid e-mail address',
      }),
    password: Joi.string()
      .min(8)
      .max(30)
      .pattern(passwordRegex)
      .required()
      .messages({
        'string.empty': 'Please enter a password',
        'string.min': 'Password must be at least 8 characters',
        'string.max': 'Password must be 30 characters or less',
        'string.pattern.base':
          'Password must contain letters, numbers, and special characters',
      }),
    confirmPassword: Joi.any()
      .valid(Joi.ref('password'))
      .required()
      .messages({
        'any.only': 'Passwords do not match',
        'any.required': 'Please confirm your password',
      }),
  });

  const form = useForm<SignupFormData>({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
    validate: joiResolver(schema),
  });

  const pending =
    googleAuthResponse.status === 'pending' ||
    emailSignupResponse.status === 'pending';

  const checks = requirements.map((requirement, index) => (
    <PasswordRequirement
      key={index}
      label={requirement.label}
      meets={requirement.re.test(form.values.password)}
    />
  ));

  const handleSubmit = async (values: SignupFormData) => {
    try {
      await emailSignup(values.email, values.password);
      onSignupSuccess?.();
    } catch (error) {
      console.error('Signup error:', error);
      // Error will be handled by getErrorMessage
    }
  };

  const getErrorMessage = (error: any) => {
    // console.log('Error details:', error); // For debugging

    // Handle known error cases with more specific messages
    const errorMessages: Record<string, { title: string; message: string; action?: string }> = {
      'auth/email-already-in-use': {
        title: 'Email Already Registered',
        message: 'An account already exists with this email address. Please use a different email or login to your existing account.',
        action: 'login'
      },
      'auth/invalid-email': {
        title: 'Invalid Email',
        message: 'Please enter a valid email address.',
      },
      'auth/weak-password': {
        title: 'Weak Password',
        message: 'Your password is too weak. Please choose a stronger password that meets all requirements.',
      },
      'auth/network-request-failed': {
        title: 'Network Error',
        message: 'Unable to connect to the server. Please check your internet connection and try again.',
      },
      'auth/too-many-requests': {
        title: 'Too Many Attempts',
        message: 'Too many unsuccessful attempts. Please wait a few minutes before trying again.',
      }
    };

    // Get error code from the error object
    const errorCode = error?.cause || 'unknown';
    // console.log('Error code:', errorCode); // For debugging

    // Get error info from our mapping or use default
    const errorInfo = errorMessages[errorCode] || {
      title: 'Email Already Exists',
      message: 'This email address is already registered. Please try logging in or use a different email address.',
      action: 'login'
    };

    return (
      <Alert
        icon={<FAIcon name="exclamation-circle" size={16} />}
        title={errorInfo.title}
        color="red"
        radius="md"
        styles={{
          title: {
            fontWeight: 600
          }
        }}
      >
        <Stack gap="xs">
          <Text size="sm">{errorInfo.message}</Text>

          {errorInfo.action === 'login' && (
            <Group justify="flex-start">
              <Button
                variant="outline"
                color="primary"
                size="small"
                leftSection={<FAIcon name="arrow-right-long" size={14} />}
                onClick={onLoginRequest}
                transition={false}
                hasArrow
                expandOnHover={false}
                component='button'
              >
                Go to Login
              </Button>
            </Group>
          )}
        </Stack>
      </Alert>
    );
  };

  // Cooldown countdown effect
  React.useEffect(() => {
    if (remainingCooldown > 0) {
      const timer = setInterval(() => {
        setRemainingCooldown((prev) => {
          if (prev <= 1) {
            setResendError(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [remainingCooldown]);

  // Resend verification email handler with server-side rate limiting
  const handleResendVerification = async () => {
    try {
      setResendingEmail(true);
      setResendSuccess(false);
      setResendError(null);

      const user = (emailSignupResponse.info as any)?.user;
      if (!user) {
        throw new Error('User not found');
      }

      // Refresh token to ensure it's not expired (force refresh)
      const token = await user.getIdToken(true);

      const rateLimitResponse = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const rateLimitData = await rateLimitResponse.json();

      if (!rateLimitResponse.ok) {
        if (rateLimitData.rateLimited) {
          // Rate limited - show countdown
          setRemainingCooldown(rateLimitData.remainingTime || 60);
          setResendError(rateLimitData.error);
          return;
        } else if (rateLimitData.alreadyVerified) {
          // Email already verified
          setResendError('Your email is already verified!');
          // Trigger verification check
          onSignupSuccess?.();
          onCloseRequest?.();
          return;
        } else {
          throw new Error(rateLimitData.error || 'Failed to send email');
        }
      }

      // Server approved - send email
      const { sendEmailVerification } = await import('firebase/auth');
      await sendEmailVerification(user, {
        url: process.env.NEXT_PUBLIC_APP_URL || window.location.origin,
        handleCodeInApp: true,
      });

      // Increment resend count for UI display
      setResendCount(prev => prev + 1);

      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000); // Hide success message after 5s

    } catch (error: any) {
      console.error('Error resending verification email:', error);

      // Handle Firebase-specific errors
      if (error?.code === 'auth/too-many-requests') {
        setResendError('Too many requests. Please wait a few minutes before trying again.');
        setRemainingCooldown(120); // 2 minutes cooldown for Firebase rate limit
      } else if (error?.code === 'auth/user-token-expired') {
        setResendError('Session expired. Please try again.');
        // Token was refreshed, so this shouldn't happen, but handle it anyway
      } else if (error?.code === 'auth/user-not-found') {
        setResendError('User account not found. Please try signing up again.');
      } else if (error?.code === 'auth/network-request-failed') {
        setResendError('Network error. Please check your connection and try again.');
      } else {
        setResendError(error instanceof Error ? error.message : 'Failed to send verification email');
      }
    } finally {
      setResendingEmail(false);
    }
  };

  // Sync rate limit status across all devices - checks every 3 seconds
  React.useEffect(() => {
    if (emailSignupResponse.status !== 'complete') return;

    const syncRateLimitStatus = async () => {
      try {
        const user = (emailSignupResponse.info as any)?.user;
        if (!user) return;

        // First check if email is already verified
        await user.reload();
        if (user.emailVerified) {
          console.log('✅ Email verified detected during sync on all devices!');
          onSignupSuccess?.();
          onCloseRequest?.();
          return; // Stop syncing if verified
        }

        const token = await user.getIdToken();

        // Use CHECK-ONLY endpoint (does NOT send email, only reads status)
        const statusResponse = await fetch('/api/auth/check-rate-limit', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });

        const statusData = await statusResponse.json();

        // If verified according to server
        if (statusData.verified) {
          console.log('✅ Email already verified (confirmed by server)');
          onSignupSuccess?.();
          onCloseRequest?.();
          return; // Stop syncing if verified
        }

        // Update UI based on rate limit status
        if (!statusData.canSend && statusData.remainingTime > 0) {
          // Still rate limited - update countdown with server time
          setRemainingCooldown(statusData.remainingTime);
          if (!resendError) {
            setResendError(statusData.message || 'Please wait before resending');
          }
        } else if (statusData.canSend) {
          // Rate limit cleared - enable button on all devices
          setRemainingCooldown(0);
          setResendError(null);
        }
      } catch (error) {
        console.error('Error syncing rate limit status:', error);
      }
    };

    // Check immediately when screen loads
    syncRateLimitStatus();

    // Then sync every 3 seconds to keep all devices in sync
    const syncInterval = setInterval(syncRateLimitStatus, 3000);

    return () => clearInterval(syncInterval);
  }, [emailSignupResponse.status, emailSignupResponse.info, resendError, onSignupSuccess, onCloseRequest]);

  // Email verification polling effect
  React.useEffect(() => {
    if (emailSignupResponse.status === 'complete') {
      // Start polling for email verification
      const checkEmailVerification = async () => {
        try {
          const user = (emailSignupResponse.info as any)?.user;
          if (!user) return;

          // Reload user to get latest emailVerified status
          await user.reload();

          if (user.emailVerified) {
            // Email is verified! Close modal and proceed
            console.log('✅ Email verified successfully!');
            onSignupSuccess?.();
            onCloseRequest?.();
          }
        } catch (error) {
          console.error('Error checking email verification:', error);
        }
      };

      // Poll every 3 seconds
      const pollInterval = setInterval(checkEmailVerification, 3000);

      // Also check immediately
      checkEmailVerification();

      // Cleanup on unmount
      return () => clearInterval(pollInterval);
    }
  }, [emailSignupResponse.status, emailSignupResponse.info, onSignupSuccess, onCloseRequest]);

  if (emailSignupResponse.status === 'complete') {
    return (
      <Root {...props} p={30}>
        <Title size={32}>Almost There!</Title>
        <Text opacity={0.7} fz={14} mt={5}>
          Thank you for signing up! We have sent you an e-mail.
        </Text>
        <Divider my={20} />
        <Alert
          icon={<FAIcon name="info-circle" size={16} />}
          title="Verification Required"
          color="blue"
          radius="md"
        >
          <Stack gap="sm">
            <Text size="sm">
              Please check your inbox and follow the instructions provided to verify
              your e-mail address.
            </Text>
            <Text size="sm" c="dimmed" fs="italic">
              This window will automatically close once you verify your email.
            </Text>
            <Group gap="xs" mt="xs">
              <FAIcon name="spinner" size={14} />
              <Text size="xs" c="dimmed">
                Waiting for verification...
              </Text>
            </Group>
          </Stack>
        </Alert>

        {resendSuccess && (
          <Alert
            icon={<FAIcon name="check-circle" size={16} />}
            title="Email Sent!"
            color="green"
            radius="md"
            mt={15}
          >
            <Text size="sm">
              Verification email has been resent. Please check your inbox.
            </Text>
          </Alert>
        )}

        {resendError && (
          <Alert
            icon={<FAIcon name="exclamation-circle" size={16} />}
            title="Cannot Send Email"
            color="orange"
            radius="md"
            mt={15}
          >
            <Stack gap="xs">
              <Text size="sm">{resendError}</Text>
              {remainingCooldown > 0 && (
                <Group gap="xs">
                  <FAIcon name="clock" size={14} />
                  <Text size="sm" fw={600}>
                    Please wait {remainingCooldown} second{remainingCooldown !== 1 ? 's' : ''}
                  </Text>
                </Group>
              )}
            </Stack>
          </Alert>
        )}

        <Box mt={20}>
          <Text size="xs" c="dimmed" ta="center" mb={15}>
            <strong>Note:</strong> You must verify your email before accessing your account.
            This window cannot be closed until verification is complete.
          </Text>

          <Divider my={15} />

          <Stack gap="xs">
            <Text size="xs" c="dimmed" ta="center">
              Didn't receive the email?
            </Text>
            <Button
              component="button"
              variant="subtle"
              color="blue"
              size="small"
              fullwidth
              onClick={handleResendVerification}
              {...(resendingEmail && { loading: true })}
              disabled={resendingEmail || resendSuccess || remainingCooldown > 0}
              leftSection={<FAIcon name="envelope" size={14} />}
              transition={false}
            >
              {resendSuccess
                ? 'Email Sent!'
                : remainingCooldown > 0
                ? `Wait ${remainingCooldown}s`
                : 'Resend Verification Email'}
            </Button>
          </Stack>
        </Box>
      </Root>
    );
  }

  return (
    <Root {...props} p={30}>
      <LoadingOverlay visible={pending} />

      <Title size={32}>Create a New Account</Title>
      <Text opacity={0.7} fz={14} mt={5}>
        Please enter your details to continue
      </Text>
      <Divider my={20} />

      {emailFormVisible ? (
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput
              required
              label={<strong>Email</strong>}
              placeholder="your.email@example.com"
              disabled={pending}
              {...form.getInputProps('email')}
              leftSection={<FAIcon name="envelope" size={14} />}
            />

            <PasswordInput
              required
              label={<strong>Password</strong>}
              placeholder="Create a strong password"
              onFocus={() => setPopoverOpened(true)}
              onBlur={() => setPopoverOpened(false)}
              disabled={pending}
              {...form.getInputProps('password')}
              leftSection={<FAIcon name="lock" size={14} />}
            />

            <PasswordInput
              required
              label={<strong>Confirm Password</strong>}
              placeholder="Re-enter your password"
              disabled={pending}
              {...form.getInputProps('confirmPassword')}
              leftSection={<FAIcon name="lock" size={14} />}
            />

            <Box>
              <Text size="sm" c="dimmed" mb={5}>
                Password requirements:
              </Text>
              {checks}
            </Box>

            {emailSignupResponse.status === 'error' && (
              <Box mt={10}>
                {getErrorMessage(emailSignupResponse.info)}
              </Box>
            )}

            <Button
              component="button"
              fullwidth
              transition={false}
              color="dark"
              type="submit"
              disabled={pending}
              {...(pending && { loading: true })}
              leftSection={<FAIcon name="user" size={14} />}
            >
              Create My Account
            </Button>
          </Stack>
        </form>
      ) : (
        <Stack>
          <Button
            component="button"
            color="default"
            fullwidth
            radius="sm"
            transition={false}
            disabled={pending}
            onClick={() => setEmailFormVisible(true)}
          >
            <Group gap={10}>
              <Box w={16} style={{ display: 'flex', alignContent: 'center' }}>
                <EmailSVG />
              </Box>
              <Text fz={14} fw={500}>
                Sign up with Email
              </Text>
            </Group>
          </Button>

          <Button
            component="button"
            color="default"
            fullwidth
            radius="sm"
            transition={false}
            disabled={pending}
            onClick={googleAuth}
          >
            <Group gap={10}>
              <Box w={16} style={{ display: 'flex', alignContent: 'center' }}>
                <GoogleSVG />
              </Box>
              <Text fz={14} fw={500}>
                Sign up with Google
              </Text>
            </Group>
          </Button>
        </Stack>
      )}

      <Divider my={20} label={<Text>or</Text>} />

      <Text size="sm" ta="center">
        Already have an account? &nbsp;
        <Anchor c="dark" opacity={0.6} onClick={onLoginRequest}>
          Login
        </Anchor>
      </Text>

      <Text size="xs" c="dark" mt={20} mb={5}>
        By proceeding, you agree to the{' '}
        <Anchor
          href="https://www.iubenda.com/terms-and-conditions/12581878"
          target="_blank"
          rel="noopener noreferrer"
          c="gray.6"
        >
          Terms and Conditions
        </Anchor>{' '}
        and{' '}
        <Anchor
          href="https://www.iubenda.com/privacy-policy/12581878"
          target="_blank"
          rel="noopener noreferrer"
          c="gray.6"
        >
          Privacy Policy
        </Anchor>
      </Text>
    </Root>
  );
};

export default SignupForm;